import { CustomerHealthPanel } from './health-panel.js';
import { MarginPanel } from './margin-panel.js';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { api, type ApiError, type RevenueMovementResult } from '@whinerp/client';
import {
  DataTable,
  EmptyState,
  ErrorState,
  MRRWaterfall,
  MetricTile,
  Panel,
  SectionLabel,
  Skeleton,
  type WaterfallStep,
} from '@whinerp/ui';
import {
  DEFAULT_FORMAT_CONTEXT,
  formatCount,
  formatMoney,
  workspaceDate,
} from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';

/**
 * SC-45 Recurring revenue.
 *
 * What MRR did, and why. Three things decide the shape of this screen.
 *
 * **Every figure is a fold over the event ledger as at a date.** Nothing here
 * reads a current-state column, because there is no current-state column to
 * read: a plan change is an entry with an effective date, so asking about last
 * quarter returns last quarter rather than today wearing a last-quarter label.
 *
 * **The waterfall is drawn only if the server says it balances.** The
 * reconciliation travels with the report and the chart refuses on its own. A
 * waterfall whose parts do not reach its total is wrong in a way that looks
 * exactly like being right, and it is the kind of picture that ends up in a
 * board pack.
 *
 * **Every step drills through.** The first question anybody asks of a churn
 * figure is which customers, and a number that cannot be interrogated does not
 * get used.
 */

type Movement = RevenueMovementResult;
type MovementKind = RevenueMovementResult['movements'][number]['kind'];

/** The waterfall's order: where it started, what moved it, where it ended. */
const MOVEMENT_KINDS: MovementKind[] = ['new', 'expansion', 'reactivation', 'contraction', 'churn'];

/** First of the month, three months back - a quarter, in the workspace's zone. */
function defaultPeriod(timeZone: string): { from: string; to: string } {
  // Only the zone matters here: this is a calendar boundary, not a rendered
  // string, and workspaceDate always emits YYYY-MM-DD.
  const today = workspaceDate(new Date(), { ...DEFAULT_FORMAT_CONTEXT, timeZone });
  const [year, month, day] = today.split('-').map(Number) as [number, number, number];
  const start = new Date(Date.UTC(year, (month ?? 1) - 1, 1));
  start.setUTCMonth(start.getUTCMonth() - 3);
  const iso = (date: Date): string => date.toISOString().slice(0, 10);
  return { from: iso(start), to: `${String(year)}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}` };
}

function RevenueScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();
  // The workspace timezone decides what "this quarter" means. Reading the
  // browser's clock would give a reader in Dubai a different quarter from a
  // reader in Los Angeles looking at the same workspace.
  const [period, setPeriod] = useState(() => defaultPeriod(format.timeZone));

  /*
   * Opening the revenue screen is what makes a renewal happen.
   *
   * There is no scheduler, so the sweep that warns about what is coming, rolls
   * forward what renews and ends what does not runs here. Each of the three
   * claims its rows with one conditional UPDATE, which is why running it on
   * every mount is safe - and a ref because this WRITES, so React's
   * development double-mount would otherwise fire it twice.
   *
   * Failure is deliberately silent: somebody opening this screen came to read
   * the numbers, and a banner about a background sweep would sit on top of
   * them.
   */
  const swept = useRef(false);
  const renewals = useMutation({ mutationFn: () => api.sweepRenewals({}) });
  const sweep = renewals.mutate;
  useEffect(() => {
    if (swept.current) return;
    swept.current = true;
    sweep();
  }, [sweep]);

  const movement = useQuery<Movement, ApiError>({
    queryKey: ['revenue-movement', period.from, period.to],
    queryFn: () => api.revenueMovement({ query: { from: period.from, to: period.to } }),
    /*
     * The previous period's report stays on screen while the next one loads.
     *
     * Without this, changing one end of the period puts the whole screen into
     * its loading state - which unmounts the date inputs the person is in the
     * middle of using. A filter that removes itself when used is not a filter.
     */
    placeholderData: keepPreviousData,
  });

  const snapshot = useQuery({
    queryKey: ['revenue-snapshot', period.to],
    queryFn: () => api.revenueSnapshot({ query: { asAt: period.to } }),
  });

  const money = (value: string, currency: string): string =>
    formatMoney(value, format, { currency });

  const steps: WaterfallStep[] = useMemo(() => {
    const report = movement.data;
    if (!report) return [];
    const counts = new Map<MovementKind, number>();
    for (const item of report.movements) counts.set(item.kind, (counts.get(item.kind) ?? 0) + 1);

    const drill = (kind: MovementKind): string =>
      `/revenue/movements?from=${report.from}&to=${report.to}&kind=${kind}`;

    return [
      {
        kind: 'opening' as const,
        label: t('revenue.opening'),
        value: money(report.openingMrr, report.currency),
        magnitude: Number(report.openingMrr),
        href: `/subscriptions?asAt=${report.from}`,
      },
      ...MOVEMENT_KINDS.map((kind) => ({
        kind,
        label: t(`revenue.${kind}` as 'revenue.new'),
        value: money(report.totals[kind], report.currency),
        magnitude: Number(report.totals[kind]),
        count: formatCount(counts.get(kind) ?? 0, format),
        href: drill(kind),
      })),
      {
        kind: 'closing' as const,
        label: t('revenue.closing'),
        value: money(report.closingMrr, report.currency),
        magnitude: Number(report.closingMrr),
        href: `/subscriptions?asAt=${report.to}`,
      },
    ];
  }, [movement.data, t, format]);

  const report = movement.data;

  return (
    <div data-component="RevenueDashboard" className="flex w-full flex-col gap-4">
      <header className="flex flex-wrap items-end gap-4">
        <SectionLabel>{t('revenue.title')}</SectionLabel>

        {/* The period. Both ends explicit: "last quarter" means different
            things to different teams, and a chart that guessed would be
            confidently wrong for half of them. */}
        <span className="flex items-end gap-2" role="group" aria-label={t('revenue.period')}>
          <label className="flex flex-col gap-0.5 text-meta text-ink-label">
            {t('revenue.from')}
            <input
              type="date"
              data-period-from
              aria-label={t('revenue.from')}
              value={period.from}
              onChange={(event) => setPeriod((current) => ({ ...current, from: event.target.value }))}
              className="h-input rounded-control border border-rule bg-surface px-2 text-dense text-ink"
            />
          </label>
          <label className="flex flex-col gap-0.5 text-meta text-ink-label">
            {t('revenue.to')}
            <input
              type="date"
              data-period-to
              aria-label={t('revenue.to')}
              value={period.to}
              onChange={(event) => setPeriod((current) => ({ ...current, to: event.target.value }))}
              className="h-input rounded-control border border-rule bg-surface px-2 text-dense text-ink"
            />
          </label>
        </span>

        {/* The tiles wait for the first report, but the period controls above
            do not: a filter that disappears while it is being used is not a
            filter. */}
        {report === undefined ? (
          <span className="ms-auto flex items-start gap-6">
            <Skeleton className="h-line-h1 w-panel-narrow" />
          </span>
        ) : (
          <span className="ms-auto flex items-start gap-6">
            <MetricTile
              data-metric="mrr"
              label={t('revenue.mrr')}
              value={money(report.closingMrr, report.currency)}
              context={t('revenue.activeCount', {
                values: { count: formatCount(snapshot.data?.active ?? 0, format) },
              })}
              labels={TREND_LABELS(t)}
              href={`/subscriptions?asAt=${report.to}`}
            />
            <MetricTile
              data-metric="arr"
              label={t('revenue.arr')}
              /* Twelve times the monthly figure, computed on the server and
                 said out loud rather than assumed by the reader. */
              value={money(snapshot.data?.arr ?? '0.00', report.currency)}
              labels={TREND_LABELS(t)}
            />
          </span>
        )}
      </header>

      {/* The revenue screens this one leads to. Declared as reached from here,
          so they are linked from here. */}
      <nav aria-label={t('revenue.more')} data-revenue-links className="flex flex-wrap gap-4 text-dense">
        <Link className="text-bronze underline" to="/revenue/invoices">{t('invoices.title')}</Link>
        <Link className="text-bronze underline" to="/revenue/renewals">{t('renewals.title')}</Link>
        <Link className="text-bronze underline" to="/revenue/churn">{t('churn.title')}</Link>
        <Link className="text-bronze underline" to="/revenue/commission" data-revenue-commission>{t('commission.title')}</Link>
      </nav>

      {/* Who needs attention before they show up in churn (G-198). */}
      <CustomerHealthPanel />
      <MarginPanel />

      {movement.isError ? (
        <RevenueError error={movement.error} retry={() => void movement.refetch()} />
      ) : report === undefined ? (
        <RevenueLoading />
      ) : (
        <>
      {steps.length === 0 ? null : (
        <Panel>
          <MRRWaterfall
            steps={steps}
            reconciliation={report.reconciliation}
            labels={{
              chart: t('revenue.chart'),
              increase: t('revenue.increase'),
              decrease: t('revenue.decrease'),
              doesNotBalance: (difference) =>
                t('revenue.doesNotBalance', {
                  values: { difference: money(difference, report.currency) },
                }),
              drillThrough: (step) => t('revenue.drillThrough', { values: { step } }),
              nothingMoved: t('revenue.nothingMoved'),
            }}
          />
        </Panel>
      )}

      {/* The movements themselves. The waterfall is the summary; this is the
          list it summarises, on the same screen, so nobody has to take the
          picture on trust. */}
      <Panel>
        <SectionLabel>{t('revenue.movement')}</SectionLabel>
        {report.movements.length === 0 ? (
          <RevenueEmpty reason="filtered-to-nothing" />
        ) : (
          <DataTable
            /*
             * Always the full set. A revenue movement table exists to reconcile
             * to a total, and a preference that hid the expansion column would
             * produce a table whose rows do not add up to the figure above it.
             */
            mode="advanced"
            data-movements-table
            label={t('revenue.movement')}
            rows={report.movements}
            rowId={(row) => row.subscriptionId}
            rowHref={(row) => `/subscriptions/${row.subscriptionId}`}
            columns={[
              {
                key: 'kind',
                header: t('revenue.movement'),
                /* The word, always. A row that said "churn" only by being red
                   says nothing in a printed pack. */
                cell: (row) => t(`revenue.${row.kind}` as 'revenue.new'),
              },
              { key: 'number', header: t('revenue.subscription'), cell: (row) => row.number },
              { key: 'account', header: t('revenue.account'), cell: (row) => row.accountName ?? '' },
              {
                key: 'amount',
                header: t('revenue.mrr'),
                align: 'end',
                cell: (row) => money(row.amount, report.currency),
              },
              { key: 'reason', header: t('revenue.reason'), cell: (row) => row.reason ?? '' },
            ]}
            labels={{
              sortBy: (column) => t('list.sortBy', { values: { column } }),
              selectRow: (row) => t('list.selectRow', { values: { row } }),
              rowsShown: (count) => t('list.rowsShown', { values: { count: formatCount(count, format) } }),
            }}
          />
        )}
      </Panel>
        </>
      )}
    </div>
  );
}

const TREND_LABELS = (t: (key: 'revenue.increase' | 'revenue.decrease' | 'revenue.nothingMoved') => string) => ({
  up: (change: string) => `${t('revenue.increase')} ${change}`,
  down: (change: string) => `${t('revenue.decrease')} ${change}`,
  flat: t('revenue.nothingMoved'),
});

function RevenueEmpty({ reason }: { reason: 'no-records' | 'filtered-to-nothing' | 'permission-denied' }): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason={reason}
      illustration="TrendUp"
      title={t('revenue.empty.title')}
      body={reason === 'filtered-to-nothing' ? t('revenue.movements.empty') : t('revenue.empty.body')}
      primary={{ label: t('empty.action.openDeals'), icon: 'Target', href: '/deals' }}
    />
  );
}

function RevenueLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex w-full flex-col gap-4" role="status" aria-live="polite" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      <Skeleton className="h-line-h1 w-block-xl" />
      <Skeleton className="h-plot w-full" />
      <Skeleton className="h-plot w-full" />
    </div>
  );
}

function RevenueError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="Recurring revenue could not be loaded"
      body={error instanceof Error ? error.message : 'The movement report did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-45',
  route: '/revenue',
  surface: 'page',
  title: 'Recurring revenue',
  features: ['F-164'],
  entitlement: 'F-164',
  reachedBy: [{ kind: 'nav', item: 'revenue' }],
  components: ['MRRWaterfall', 'MetricTile', 'DataTable'],
  elements: ['RevenueDashboard'],
  states: {
    empty: RevenueEmpty,
    loading: RevenueLoading,
    error: RevenueError,
  },
  component: RevenueScreen,
});

export default screen;
